import { DropdownOption, stringArrayToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import { ContractDocumentDto, ContractDocumentSearchRequest, UpsertContractDocumentDto } from 'ts-types/api.types';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { FormState } from 'final-form';
import React, { Component } from 'react';
import axios, { CancelTokenSource } from 'axios';
import { findContractDocumentsById, searchContractDocuments } from 'services/contractDocumentService';
import { noop } from 'utils/functionUtils';
import { debounce } from 'ts-debounce';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import LoaderComponent from 'components/LoaderComponent';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import PxGrid from 'components/layout/PxGrid';
import { Grid, Modal } from 'semantic-ui-react';
import Select from 'components/final-form/Select';
import AddButton from 'components/AddButton';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import EditActionButton from 'components/EditActionButton';
import UpsertContractDocumentForm from 'components/guarantors/contract-document/UpsertContractDocumentForm';
import SearchResultComponent from 'components/SearchResultComponent';

interface ContractDocumentFieldProps extends FormState {
  values: Partial<ContractDocumentSearchRequest>
}

interface Props extends MessageSourceProps {
}

interface State {

  foundContractDocuments: Array<ContractDocumentDto>,
  contractDocumentsLoaded: boolean,
  addEditModalOpen: boolean,
  cancelTokenSource: CancelTokenSource,
  contractDocument?: UpsertContractDocumentDto,
  searchValues: Partial<ContractDocumentSearchRequest>
  contractDocumentTypes: Array<DropdownOption>,
  contractDocumentId?: number
}

class ContractDocumentView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const {getMessage} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    const contractDocumentTypeKeys: Array<string> = ["ALL", "TERMS_OF_SERVICE", "DATA_PROTECTION"];
    const contractDocumentTypeLabels: Array<string> = [
      "ALL", "TERMS_OF_SERVICE", "DATA_PROTECTION"];

    const contractDocumentTypes = stringArrayToDropdownOptionArray(
        contractDocumentTypeLabels,
        getMessage,
        "contractDocumentType",
        contractDocumentTypeKeys
    );

    this.state = {
      foundContractDocuments: [],
      contractDocumentsLoaded: false,
      searchValues: {},
      addEditModalOpen: false,
      contractDocumentTypes: contractDocumentTypes,
      cancelTokenSource
    };

  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (id: number | undefined) => {

    const {cancelTokenSource} = this.state;

    if (id) {
      findContractDocumentsById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }

  }

  showModal = (id?: number, contractDocument?: UpsertContractDocumentDto) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        contractDocument: contractDocument,
        contractDocumentId: id
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      contractDocument: undefined,
      contractDocumentId: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    const onFinaly = () => {
      this.setState({
        contractDocumentsLoaded: true
      })
    }

    searchContractDocuments(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundContractDocuments: response
          });
        })
        .catch(noop)
        .finally(onFinaly)
    this.hideModal();
  };

  handleChange = debounce(({values}: ContractDocumentFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const {contractDocumentTypes} = this.state;

    const onFinally = () => {
      this.setState({
        contractDocumentsLoaded: true
      })
    };

    const {searchValues, cancelTokenSource} = this.state;

    this.setState({contractDocumentsLoaded: false});

    searchContractDocuments(values, cancelTokenSource)
        .then(response => {
          this.setState({
            foundContractDocuments: response,
            searchValues: values
          })
        })
        .catch(noop)
        .finally(onFinally);

  }, 300);

  render(): React.ReactNode {
    const {contractDocumentsLoaded} = this.state;
    const {getMessage} = this.props;

    return (
        <PraxStyledSegment style={{height: "100%"}}>
          <div>
            {this.renderSearchContractDocumentSegment()}
          </div>
          <StyledViewTableRow>
            {!contractDocumentsLoaded
                ? <LoaderComponent message={getMessage("contractDocument.loading")}/>
                : this.renderContractDocumentsSegment()}
          </StyledViewTableRow>
          {this.renderUpsertContractDocumentModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchContractDocumentSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{}}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {contractDocumentTypes, foundContractDocuments} = this.state;

    const numResults = foundContractDocuments.length;

    return (
        <PxGrid columns={2}>
          <Grid.Row>
            <Grid.Column width={3}>
              {getMessage("contractDocument.contractDocumentType")}:
            </Grid.Column>
            <Grid.Column width={6}>
              <Field
                  name="contractDocumentType"
                  component={Select}
                  fluid
                  options={contractDocumentTypes}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16} textAlign="right">
              <AddButton buttonMessage={getMessage("button.add")}
                         onClick={() => this.showModal()}
              />
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    )
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
        </React.Fragment>
    );
  };

  contractDocumentRowGetter = ({index}: any) => {
    const {foundContractDocuments} = this.state;

    Object.assign(foundContractDocuments[index], {index: index + 1});

    return foundContractDocuments[index];
  };

  contractDocumentRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    const {foundContractDocuments} = this.state;

    const employeeId = foundContractDocuments[index].id;

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(employeeId)}
        >
          {columns}
        </div>
    );
  };

  renderContractDocumentsSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundContractDocuments} = this.state;
    return (
        <VirtualizedTable
            rowCount={foundContractDocuments.length}
            rowGetter={this.contractDocumentRowGetter}
            rowRenderer={this.contractDocumentRowRenderer}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 200,
                label: (getMessage("contractDocument.documentType")),
                dataKey: "documentType"
              },
              {
                width: 100,
                label: (getMessage("contractDocument.documentVersion")),
                dataKey: "documentVersion"
              },
              {
                width: 150,
                label: (getMessage("contractDocument.validFromDate")),
                dataKey: "validFromDate"
              },
              {
                width: 250,
                label: (getMessage("contractDocument.descriptionDe")),
                dataKey: "descriptionDe"
              },
              {
                width: 350,
                label: (getMessage("contractDocument.documentUrlDe")),
                dataKey: "documentUrlDe"
              },
              {
                width: 250,
                label: (getMessage("contractDocument.descriptionFr")),
                dataKey: "descriptionFr"
              },
              {
                width: 350,
                label: (getMessage("contractDocument.documentUrlFr")),
                dataKey: "documentUrlFr"
              },
              {
                width: 250,
                label: (getMessage("contractDocument.descriptionIt")),
                dataKey: "descriptionIt"
              },
              {
                width: 350,
                label: (getMessage("contractDocument.documentUrlIt")),
                dataKey: "documentUrlIt"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />

    );
  };

  renderUpsertContractDocumentModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, contractDocument, contractDocumentId} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("contractDocument.title")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertContractDocumentForm
                contractDocument={contractDocument}
                contractDocumentId={contractDocumentId}
                onCancel={this.hideModal}
                onSave={this.onSaveUpsertForm}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

}

export default withMessages(ContractDocumentView);
